import { LinkIcon } from "@chakra-ui/icons";
import { Button, Icon } from "@chakra-ui/react";
import { TableViewCreateOrUpdateModal } from "features/transactions/TableViewCreateOrUpdateModal/TableViewCreateOrUpdateModal";
import {
  getTableViewsGetQueryKey,
  useTableViewUpdate,
} from "gen/endpoints/table-views/table-views";
import { useUsersMeRetrieveSuspense } from "gen/endpoints/users/users";
import { TableParamsTypeEnum } from "gen/model/tableParamsTypeEnum";
import { TableViewReadData } from "gen/model/tableViewReadData";
import { compact } from "lodash-es";
import { useTranslation } from "react-i18next";
import { FaFileCirclePlus } from "react-icons/fa6";
import { useToast } from "shared/components/toast/useToast";
import { useInvalidateQueryKeys } from "shared/utils/QueryUtils";
import { useBoolean } from "usehooks-ts";

export const useTableViewActions = ({
  view,
  isDraft,
  syncTableStateWithCurrentView,
  canSave,
  getViewState,
}: {
  view?: TableViewReadData;
  isDraft: boolean;
  syncTableStateWithCurrentView: () => void;
  canSave: boolean;
  getViewState: () => Promise<string>;
}) => {
  const { data: userData } = useUsersMeRetrieveSuspense();
  const isMine = view?.created_by === userData.id;
  const editingMyOwnView = isMine && !isDraft;
  const editingNotMyView = !isMine && !isDraft;

  const { mutateAsync: updateTableView, isPending: isUpdatePending } =
    useTableViewUpdate();

  const { showToast } = useToast();
  const { t } = useTranslation(["views", "common"]);

  const updateView = async () => {
    if (!view) {
      return;
    }
    await updateTableView({
      viewId: view.id,
      data: {
        name: view.name,
        encoded_table_params: await getViewState(),
      },
    });
    syncTableStateWithCurrentView();
    showToast({
      message: t("views:toasts.viewUpdated"),
      variant: "success",
    });
  };
  const copyShareLink = async () => {
    const url = new URL(window.location.toString());
    await navigator.clipboard.writeText(url.toString());
    showToast({
      message: t("common:linkCopiedToClipboard"),
      variant: "success",
    });
  };
  const {
    value: isCreateModalOpen,
    setTrue: openCreateModal,
    setFalse: closeCreateModal,
  } = useBoolean();

  const actions = compact([
    isDraft && (
      <Button
        size="xs"
        colorScheme="primary"
        onClick={openCreateModal}
        key="save"
        isDisabled={!canSave}
      >
        {t("views:actions.saveAsNew")}
      </Button>
    ),
    !isDraft && (
      <Button
        size="xs"
        colorScheme="secondary"
        key="share"
        onClick={copyShareLink}
        leftIcon={<LinkIcon />}
      >
        {t("views:actions.share")}
      </Button>
    ),
    (editingNotMyView || editingMyOwnView) && (
      <Button
        size="xs"
        isDisabled={!canSave}
        colorScheme="tertiary"
        key="save-new"
        onClick={openCreateModal}
        leftIcon={
          <Icon
            as={FaFileCirclePlus}
            color="teal.900"
            width="15px"
            height="15px"
          />
        }
      >
        {t("views:actions.saveAsNew")}
      </Button>
    ),
    editingMyOwnView && (
      <Button
        size="xs"
        isDisabled={!canSave}
        colorScheme="primary"
        key="edit"
        onClick={updateView}
        isLoading={isUpdatePending}
      >
        {t("views:actions.saveUpdates")}
      </Button>
    ),
  ]);

  const invalidateQuery = useInvalidateQueryKeys();
  const onCreateNew = async () => {
    await invalidateQuery(getTableViewsGetQueryKey());
    showToast({
      message: t("views:toasts.viewCreated"),
      variant: "success",
    });
  };

  return (
    <>
      {actions}
      <TableViewCreateOrUpdateModal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        viewType={TableParamsTypeEnum.transaction_table}
        getViewState={getViewState}
        onSuccess={onCreateNew}
      />
    </>
  );
};
